import { getPrimeFromPaymentModule } from '@cart-services/core/paymentService'
import type * as CheckoutPay from '@dni-types/payment/checkoutpay'
import type * as TapPay from '@dni-types/payment/tappay'
import { type ApplePayPaymentToken } from '@hooks-api/types/apiRequest'
import { type Nullable } from '@hooks-api/types/nullable'
import { type CheckoutPayAlipayHkModule, type CheckoutPayApplePayModule, type CheckoutPayCreditCardModule, type CheckoutPayWeChatPayHkModule, type ECPayCreditCardModule, type TappayApplePayModule, type TappayCreditCardModule, type TappayJkoPayModule, type TappayLinePayModule } from '@payments'
import { isCkoFlowModule } from '@payments/lib/checkout-pay/isCkoFlowModule'

/**
 * 非 Flow SDK 路線的 module — 共通介面是 `getPrime()` 拿 pay token，FE 直接帶 token 給 BE。
 * Tappay / ECPay 屬於此類。
 */
export type NonFlowPaymentModule =
  | TappayCreditCardModule
  | TappayJkoPayModule
  | TappayApplePayModule
  | TappayLinePayModule
  | ECPayCreditCardModule

/**
 * CheckoutPay Flow SDK 路線的 module — 共通介面是 session-based lifecycle（mount / submit），
 * 沒有 `getPrime()`，由 SDK 內部處理 token。不能跟 NonFlowPaymentModule 共用同樣的 helper。
 */
export type FlowPaymentModule =
  | CheckoutPayCreditCardModule
  | CheckoutPayApplePayModule
  | CheckoutPayAlipayHkModule
  | CheckoutPayWeChatPayHkModule

export type AppliedPaymentModule = NonFlowPaymentModule | FlowPaymentModule

/** Type guard：把 AppliedPaymentModule narrow 成 NonFlowPaymentModule，給 caller 安全傳入 `getPaymentModulePrime`。 */
export const isNonFlowPaymentModule = (paymentModule: Nullable<AppliedPaymentModule>): paymentModule is NonFlowPaymentModule =>
  paymentModule != null && !isCkoFlowModule(paymentModule)

export type PaymentModulePrime =
  | { paymentPrime: string, cardIdentifier?: string }
  | { applePayInfo: { paymentData: ApplePayPaymentToken } }

/**
 * 取得 pay token / Apple Pay info。**只接受 NonFlowPaymentModule**：
 * Flow SDK 走 session lifecycle，沒有 prime 概念，由 TS 型別擋掉誤呼叫。
 */
export const getPaymentModulePrime = async (paymentModule: Nullable<NonFlowPaymentModule>): Promise<PaymentModulePrime> => {
  if (!paymentModule) return { paymentPrime: '' }
  return (await getPrimeFromPaymentModule(paymentModule)) ?? { paymentPrime: '' }
}

export const getCardNumber = (
  card: Nullable<CheckoutPay.FrameCardTokenizedEvent | TapPay.ValidationResult['card']>,
): string => {
  if (!card) return ''

  if ('last4' in card && card.last4) {
    return card.last4
  }

  if ('lastfour' in card && card.lastfour) {
    return card.lastfour
  }

  return ''
}

export const getPaymentCardNumber = (paymentModule: Nullable<AppliedPaymentModule>) => {
  if (!paymentModule) return ''

  const card
    = 'getCard' in paymentModule && typeof paymentModule.getCard === 'function'
      ? paymentModule.getCard()
      : undefined

  return getCardNumber(card)
}
